import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  MapPin,
  Clock,
  Video,
  Sparkles,
  Leaf,
  HeartHandshake,
  Shield,
  Calendar as CalendarIcon,
  Users,
  Sprout,
  Cloud,
  Compass,
  BookOpen,
  Wind,
  Sunrise,
  Building2,
  Flame,
  Heart,
  Waves,
  Mountain,
  Moon,
  Sun,
  Anchor,
  Feather,
  Star,
  ShieldCheck,
  LifeBuoy,
  Phone,
  AlertTriangle,
} from "lucide-react";

const AREA_ICONS = {
  Shield, Sprout, Cloud, Compass, BookOpen, Wind,
  Sunrise, Flame, Heart, Users, HeartHandshake, Leaf,
  Waves, Mountain, Sparkles, Moon, Sun, Anchor, Feather, Star, ShieldCheck,
};

const SERVICE_COVERS = {
  "individual-adult": "/adult-session.png",
  "youth": "/youth-session.png",
};

// Standard Latin crucifix — taller vertical, shorter beam at upper third.
function Crucifix({ size = 36, className = "", strokeWidth = 1.5 }) {
  // viewBox is 12 wide × 24 tall (1:2 ratio). Crossbar at y=8 (upper third).
  return (
    <svg
      viewBox="0 0 12 24"
      width={size / 2}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      className={className}
      aria-hidden
    >
      <line x1="6" y1="1.75" x2="6" y2="22.25" />
      <line x1="1.75" y1="8" x2="10.25" y2="8" />
    </svg>
  );
}

// Australian crisis lines & resources (fixed, vetted).
const CRISIS_LINES = [
  {
    name: "Lifeline",
    phone: "13 11 14",
    href: "tel:131114",
    web: "lifeline.org.au",
    weburl: "https://www.lifeline.org.au",
    desc: "24/7 crisis support and suicide prevention.",
    badge: "24/7",
  },
  {
    name: "Beyond Blue",
    phone: "1300 22 4636",
    href: "tel:1300224636",
    web: "beyondblue.org.au",
    weburl: "https://www.beyondblue.org.au",
    desc: "Mental health support — anxiety, depression, and more.",
    badge: "24/7",
  },
  {
    name: "Kids Helpline",
    phone: "1800 55 1800",
    href: "tel:1800551800",
    web: "kidshelpline.com.au",
    weburl: "https://kidshelpline.com.au",
    desc: "Free, private counselling for ages 5 – 25.",
    badge: "24/7",
  },
  {
    name: "Suicide Call Back Service",
    phone: "1300 659 467",
    href: "tel:1300659467",
    web: "suicidecallbackservice.org.au",
    weburl: "https://www.suicidecallbackservice.org.au",
    desc: "Counselling for anyone affected by suicide.",
    badge: "24/7",
  },
  {
    name: "13YARN",
    phone: "13 92 76",
    href: "tel:139276",
    web: "13yarn.org.au",
    weburl: "https://www.13yarn.org.au",
    desc: "Crisis support for Aboriginal & Torres Strait Islander peoples.",
    badge: "24/7",
  },
  {
    name: "1800RESPECT",
    phone: "1800 737 732",
    href: "tel:1800737732",
    web: "1800respect.org.au",
    weburl: "https://www.1800respect.org.au",
    desc: "National sexual assault, family & domestic violence counselling.",
    badge: "24/7",
  },
];

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EnquiryForm from "@/components/EnquiryForm";
import EmailCopy from "@/components/EmailCopy";
import { api, BRAND, trackEvent } from "@/lib/api";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.8, ease: "easeOut" },
};

export default function Landing() {
  const [services, setServices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [areas, setAreas] = useState([]);
  const [content, setContent] = useState({});

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/areas").then((r) => setAreas(r.data)).catch(() => {});
    api.get("/content").then((r) => setContent(r.data || {})).catch(() => {});
    trackEvent("page_view", "/");
  }, []);

  // Content helper with fallback
  const c = (key, fallback = "") =>
    content[key] !== undefined && content[key] !== "" ? content[key] : fallback;

  return (
    <div className="bg-cream text-ink">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-[-10%] right-[-10%] w-[620px] h-[620px] rounded-full bg-navy-light/15 blur-3xl" />
          <div className="absolute bottom-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-steel/20 blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div {...fadeUp} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-cream-soft border border-sand/70 px-4 py-1.5 text-xs tracking-[0.2em] uppercase text-ink-muted mb-8">
              <Leaf size={14} className="text-navy" />
              Now accepting new clients
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-ink text-balance">
              A space for you<br />
              <span className="text-navy">with compassion &amp; care.</span>
            </h1>
            <p className="mt-7 text-lg text-ink-muted max-w-2xl leading-relaxed font-light">
              A Mindful Space Counselling, based in Sorell, offers compassionate
              online and face-to-face counselling — a safe, non-judgmental space
              for people of all ages and backgrounds, where you can pause the
              busyness, return to the present, and reconnect with what matters.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/book"
                data-testid="hero-book-button"
                className="group inline-flex items-center gap-2 rounded-full bg-navy text-cream px-7 py-4 text-sm font-medium hover:bg-navy-deep hover:-translate-y-0.5 shadow-[0_14px_34px_-14px_rgba(14,59,74,0.8)] transition-all duration-300"
              >
                Book a session
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="#meet-jesse"
                data-testid="hero-learn-more"
                className="inline-flex items-center gap-2 rounded-full border border-ink/25 text-ink px-7 py-4 text-sm font-medium hover:border-navy hover:text-navy transition-all duration-300"
              >
                Meet Jesse
              </a>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { n: content["hero.stat_1_number"] ?? "60", l: content["hero.stat_1_label"] ?? "minute sessions" },
                { n: content["hero.stat_2_number"] ?? "2", l: content["hero.stat_2_label"] ?? "locations" },
                { n: content["hero.stat_3_number"] ?? "Online", l: content["hero.stat_3_label"] ?? "available", small: true },
              ].map((s, i) => (
                <div key={`hero-stat-${i}`} data-testid={`hero-stat-${i + 1}`}>
                  <div className={`font-serif ${s.small ? "text-3xl" : "text-4xl"} text-navy`}>{s.n}</div>
                  <div className="text-xs uppercase tracking-[0.16em] text-ink-soft mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.1 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] max-w-md mx-auto">
              <div className="absolute -inset-6 rounded-[3rem] bg-gradient-to-br from-steel/25 to-navy-light/20 rotate-3" />
              <div className="absolute -inset-3 rounded-[2.5rem] bg-cream-soft rotate-1 shadow-[0_30px_80px_-30px_rgba(7,42,55,0.45)]" />
              <img
                src={BRAND.portraitUrl}
                alt="Jesse Winter — Counsellor"
                data-testid="hero-portrait"
                className="relative rounded-[2.25rem] w-full h-full object-cover"
              />
              <div className="absolute -bottom-8 -left-6 sm:-left-10 bg-cream-soft/95 backdrop-blur-sm border border-sand/80 rounded-3xl p-6 shadow-[0_24px_60px_-30px_rgba(7,42,55,0.45)] max-w-[320px] sm:max-w-[400px]">
                <div className="flex items-center gap-2 mb-3">
                  <span className="h-2 w-2 rounded-full bg-navy animate-pulse" />
                  <span className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">In Jesse's words</span>
                </div>
                <div className="font-serif text-[15px] leading-[1.55] text-ink">
                  “I love being in the room with people. Not because of hearing the details
                  of people's stories — but because of the honour and privilege to sit in the
                  deepest parts of people's world. Then, journeying with the same person to
                  move forward and take steps that, at once, they did not think or believe
                  would be possible.”
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-sand/60 bg-cream-soft/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-7 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
          {[
            { icon: Shield, t: "Fully confidential" },
            { icon: HeartHandshake, t: "Person-centred approach" },
            { icon: Video, t: "Telehealth available" },
            { icon: Sparkles, t: "No diagnosis required" },
          ].map(({ icon: Icon, t }) => (
            <div key={t} className="flex items-center gap-3 text-ink-muted">
              <Icon size={18} strokeWidth={1.5} className="text-navy" />
              <span>{t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* MEET JESSE */}
      <section id="meet-jesse" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-start">
          {/* Left: photo + pull-quote */}
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-5 rounded-[3rem] bg-gradient-to-br from-navy-light/15 to-steel/15 rotate-2" aria-hidden />
              <div className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-[0_30px_80px_-30px_rgba(7,42,55,0.45)]">
                <img
                  src={BRAND.lifestyleUrl}
                  alt="Jesse Winter on the Tasmanian coast"
                  data-testid="meet-jesse-photo"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="mt-10 border-l-2 border-navy pl-6">
              <p className="font-serif text-2xl lg:text-[26px] text-ink leading-snug text-balance">
                “You aren't a problem to be solved; you are a human being.”
              </p>
            </div>
          </motion.div>

          {/* Right: content */}
          <motion.div {...fadeUp} className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.22em] text-navy mb-5">Meet Jesse</div>
            <h2 className="font-serif text-6xl lg:text-7xl leading-[0.95] text-ink mb-4 tracking-tight">
              Jesse
            </h2>
            <div className="font-sans text-xs sm:text-sm uppercase tracking-[0.28em] text-ink-muted mb-8">
              ACA Registered Counsellor
            </div>
            <div className="h-px w-16 bg-navy mb-10" />

            <div className="space-y-6 text-ink-muted leading-relaxed font-light text-[17px]">
              <p>
                Jesse's approach to counselling is deeply holistic. He believes that the
                person is always more important than the “presenting issue.” His goal isn't
                to fix a problem, but to work with the human being behind it. He understands
                that whatever you bring to the table doesn't exist in isolation — whether it
                involves family, work, or home, every facet of our lives is intertwined to
                make us who we are.
              </p>
              <p>
                With a Diploma of Counselling and Level 1 registration with the ACA, Jesse
                is passionate about walking alongside people and navigating their unique
                journeys together.
              </p>
              <div className="pt-2 pl-6 border-l border-sand">
                <p className="text-ink/85 italic">
                  On a personal note, Jesse is a husband and a father of two. Life at home
                  is kept busy by their family dog, Maverick — and when he has a moment of
                  downtime, Jesse is an avid fan of tennis and board games.
                </p>
              </div>
            </div>

            <div className="mt-12 grid sm:grid-cols-2 gap-4">
              {[
                { t: "Registered Counsellor", s: "Australian Counselling Association" },
                { t: "Member #86410", s: "Verified ACA practitioner" },
              ].map((c) => (
                <div key={c.t} className="bg-cream-soft border border-sand/80 rounded-2xl p-5">
                  <div className="font-serif text-lg text-navy">{c.t}</div>
                  <div className="text-xs text-ink-soft mt-1">{c.s}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CHRIST IS LORD */}
      <section
        id="faith"
        data-testid="faith-section"
        className="relative overflow-hidden bg-navy-deep"
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-navy-light/15 blur-3xl pointer-events-none" aria-hidden />

        <div className="relative max-w-3xl mx-auto px-6 lg:px-10 py-28 lg:py-40 text-center text-cream">
          <motion.div {...fadeUp}>
            <div className="mb-9 text-teal-mist flex justify-center" data-testid="faith-crucifix">
              <Crucifix size={56} strokeWidth={1.4} />
            </div>
            {c("faith.eyebrow") && (
              <div className="text-xs uppercase tracking-[0.28em] text-teal-mist mb-6">
                {c("faith.eyebrow")}
              </div>
            )}
            <h2 className="font-serif text-6xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight mb-6 text-cream">
              {c("faith.headline_1", "Christ is")}{" "}
              <span className="italic text-teal-mist">
                {c("faith.headline_2", "LORD")}
              </span>
              .
            </h2>
            {c("faith.tagline") && (
              <p
                data-testid="faith-tagline"
                className="font-serif italic text-xl sm:text-2xl lg:text-[26px] text-teal-mist/90 leading-snug max-w-2xl mx-auto mb-10 text-balance"
              >
                {c("faith.tagline")}
              </p>
            )}
            <div className="mx-auto h-px w-20 bg-teal-mist/50 mb-12" />

            <div className="space-y-7 text-[17px] lg:text-lg text-cream/85 leading-[1.75] font-light max-w-2xl mx-auto">
              <p data-testid="faith-p1">{c("faith.p1")}</p>
              <p data-testid="faith-p2">{c("faith.p2")}</p>
              <p className="text-cream/95" data-testid="faith-p3">{c("faith.p3")}</p>
            </div>

            {c("faith.quote") && (
              <div className="mt-14 pt-10 border-t border-cream/15 max-w-xl mx-auto">
                <p className="font-serif italic text-2xl lg:text-[26px] text-cream leading-snug text-balance">
                  &ldquo;{c("faith.quote")}&rdquo;
                </p>
                {c("faith.quote_ref") && (
                  <div className="mt-3 text-[11px] uppercase tracking-[0.28em] text-teal-mist">
                    {c("faith.quote_ref")}
                  </div>
                )}
              </div>
            )}

            {c("faith.closing") && (
              <div className="mt-12 text-sm text-cream/65 max-w-md mx-auto">
                {c("faith.closing")}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="relative">
        <div
          className="absolute inset-0 -z-10 bg-cream-soft/60 border-y border-sand/60"
          aria-hidden
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <motion.div {...fadeUp} className="max-w-2xl mb-16">
            <div className="text-xs uppercase tracking-[0.22em] text-navy mb-4">Sessions</div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-tight text-ink mb-6">
              What sessions look like.
            </h2>
            <p className="text-ink-muted leading-relaxed font-light">
              Every session is 60 minutes of undivided attention — whether we meet
              in person or online. Pick whatever feels right on the day.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((s, i) => {
              const cover = SERVICE_COVERS[s.slug];
              return (
              <motion.div
                key={s.slug}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                data-testid={`service-card-${s.slug}`}
                className="group relative bg-cream rounded-[2rem] overflow-hidden border border-sand/70 hover:border-navy/60 transition-all duration-500 hover:-translate-y-1 flex flex-col"
              >
                {cover && (
                  <div className="relative aspect-[16/9] overflow-hidden bg-cream-soft">
                    <img
                      src={cover}
                      alt=""
                      data-testid={`service-image-${s.slug}`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-cream/40 via-transparent to-transparent" />
                  </div>
                )}
                <div className="p-10 flex flex-col flex-grow">
                  <div className="flex items-baseline justify-between mb-6">
                    <span className="text-xs uppercase tracking-[0.22em] text-ink-soft">{s.audience}</span>
                  </div>
                  <h3 className="font-serif text-3xl text-ink mb-3 leading-tight">{s.name}</h3>
                  <p className="text-ink-muted leading-relaxed font-light mb-7">{s.description}</p>
                  <div className="flex items-center gap-4 text-xs uppercase tracking-[0.18em] text-ink-soft mb-8">
                    <span className="inline-flex items-center gap-1.5"><Clock size={13} />{s.duration_min} min</span>
                    <span>•</span>
                    <span>Online or in-person</span>
                  </div>
                  <Link
                    to={`/book?service=${s.slug}`}
                    data-testid={`service-book-${s.slug}`}
                    className="inline-flex items-center gap-2 text-navy font-medium text-sm group-hover:gap-3 transition-all mt-auto"
                  >
                    Book {s.audience.split(" ")[0].toLowerCase()} session
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AREAS OF SUPPORT */}
      <section id="areas" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <motion.div {...fadeUp} className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] text-navy mb-4">Areas of support</div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-tight text-ink">
              What we'll walk through together.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-ink-muted leading-relaxed font-light">
              You don't need a label or a diagnosis to start. These are common reasons
              people first reach out — and you're welcome here whether your story fits
              one of these or none of them.
            </p>
          </div>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {areas.map((a, i) => {
            const Icon = AREA_ICONS[a.icon] || Sparkles;
            return (
              <motion.div
                key={a.id}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: (i % 3) * 0.08 }}
                data-testid={`area-card-${a.slug}`}
                className="group bg-cream-soft border border-sand/70 rounded-3xl p-8 md:p-10 hover:border-navy/60 hover:-translate-y-0.5 transition-all duration-500 flex items-start gap-5 min-h-[180px]"
              >
                <div className="flex-shrink-0 h-12 w-12 rounded-2xl bg-navy/8 text-navy flex items-center justify-center group-hover:bg-navy group-hover:text-cream transition-colors duration-500">
                  <Icon size={22} strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-3xl md:text-4xl text-ink leading-[1.05] tracking-tight self-center">
                  {a.name}
                </h3>
              </motion.div>
            );
          })}
        </div>

        {/* Groups & Businesses callout */}
        <motion.div
          {...fadeUp}
          data-testid="groups-callout"
          className="mt-14 bg-navy-deep text-cream rounded-[2rem] p-8 lg:p-12 grid lg:grid-cols-12 gap-8 items-center overflow-hidden relative"
        >
          <div className="absolute -right-10 -bottom-10 h-56 w-56 rounded-full bg-navy-light/30 blur-3xl pointer-events-none" />
          <div className="lg:col-span-7 relative">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-cream/70 mb-4">
              <Building2 size={14} /> For groups & workplaces
            </div>
            <h3 className="font-serif text-3xl lg:text-4xl leading-tight mb-4 text-cream">
              Beyond 1:1 — counselling for groups & teams in the Sorell area.
            </h3>
            <p className="text-cream/80 leading-relaxed font-light max-w-xl">
              We provide counselling services across the Sorell region. If you'd like to
              organise something more than individual counselling — for a workplace, group,
              school, or community — please reach out via the contact form or directly to
              our email.
            </p>
          </div>
          <div className="lg:col-span-5 relative flex flex-col gap-3">
            <a
              href="#enquiry"
              data-testid="groups-enquire-button"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-cream text-navy-deep px-6 py-4 text-sm font-medium hover:bg-cream-soft hover:-translate-y-0.5 transition-all duration-300"
            >
              Send an enquiry <ArrowRight size={14} />
            </a>
            <div className="inline-flex items-center justify-center rounded-full border border-cream/30 px-6 py-4 text-sm">
              <EmailCopy
                email={BRAND.email}
                tone="dark"
                showIcon={false}
                testid="groups-email"
              />
            </div>
          </div>
        </motion.div>
      </section>

      {/* TRAUMA-INFORMED SECTION */}
      <section
        id="trauma"
        data-testid="trauma-section"
        className="relative overflow-hidden"
      >
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-cream-soft/70" />
          <div className="absolute top-0 right-0 h-72 w-72 rounded-full bg-navy-light/15 blur-3xl" />
          <div className="absolute bottom-0 left-1/4 h-80 w-80 rounded-full bg-sage/20 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <motion.div {...fadeUp} className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="inline-flex items-center gap-2 rounded-full bg-navy/8 text-navy px-4 py-1.5 text-xs uppercase tracking-[0.22em] mb-6" data-testid="trauma-eyebrow">
              <ShieldCheck size={14} /> Trauma-informed care
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05] text-ink mb-7 text-balance">
              A space that understands<br />
              <span className="text-navy">what you carry.</span>
            </h2>
            <p className="text-ink-muted leading-relaxed font-light max-w-md mb-8">
              Trauma can shape the way we feel safe in our bodies, our relationships, and
              the world. The work we do together is grounded in trauma-informed
              principles — your story, your pace, your voice.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/book"
                data-testid="trauma-book-button"
                className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-6 py-3.5 text-sm font-medium hover:bg-navy-deep hover:-translate-y-0.5 transition-all duration-300"
              >
                Start with a session <ArrowRight size={14} />
              </Link>
              <a
                href="#enquiry"
                data-testid="trauma-enquire-link"
                className="inline-flex items-center gap-2 rounded-full border border-ink/25 text-ink px-6 py-3.5 text-sm font-medium hover:border-navy hover:text-navy transition"
              >
                Ask a question first
              </a>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="lg:col-span-6 lg:col-start-7 space-y-5">
            {[
              {
                icon: Shield,
                title: "Safety first, always.",
                body: "Sessions are paced around what feels manageable for you. There's no pressure to share more than you're ready to — and we'll regularly check in about what's working.",
              },
              {
                icon: HeartHandshake,
                title: "Choice, voice, and control.",
                body: "You decide the topics, the pace, and how we use our time. Trauma often takes our sense of agency; this space is built to give it back.",
              },
              {
                icon: Sparkles,
                title: "The whole story matters.",
                body: "Trauma isn't only big, single events — it can also be the slow weight of stress, relational pain, spiritual harm, or grief. All of it is welcome here.",
              },
              {
                icon: Leaf,
                title: "Body, mind & spirit.",
                body: "We work with what shows up — anxiety in the chest, the racing thoughts, the heaviness, the disconnection — and gentle, evidence-informed ways to move toward feeling more like yourself.",
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                  data-testid={`trauma-pillar-${i}`}
                  className="bg-cream rounded-3xl border border-sand/70 p-6 sm:p-7 flex gap-5 hover:border-navy/40 transition"
                >
                  <div className="flex-shrink-0 h-12 w-12 rounded-2xl bg-navy/8 text-navy flex items-center justify-center">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl text-ink leading-tight mb-2">{item.title}</h3>
                    <p className="text-ink-muted leading-relaxed font-light text-sm">{item.body}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* WELLBEING CHECK CTA */}
      <section
        id="wellbeing-check"
        data-testid="wellbeing-cta-section"
        className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28"
      >
        <motion.div
          {...fadeUp}
          className="relative bg-navy-deep text-cream rounded-[2.5rem] p-8 lg:p-14 grid lg:grid-cols-12 gap-8 items-center overflow-hidden"
        >
          <div className="absolute -left-20 -top-20 h-72 w-72 rounded-full bg-navy-light/30 blur-3xl pointer-events-none" />
          <div className="absolute -right-10 -bottom-20 h-72 w-72 rounded-full bg-sage/25 blur-3xl pointer-events-none" />

          <div className="lg:col-span-7 relative">
            <div className="inline-flex items-center gap-2 rounded-full bg-cream/12 px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] text-cream/80 mb-6">
              <Sparkles size={14} /> 2-minute reflection · free & private
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mb-5 text-balance">
              How are you, really?
            </h2>
            <p className="text-cream/80 leading-relaxed font-light max-w-xl">
              A short, ten-question Holistic Wellbeing Check — a moment to pause and
              notice how you've been across the past fortnight. Nothing is stored;
              your answers are just for you.
            </p>
          </div>
          <div className="lg:col-span-5 relative flex flex-col gap-3 lg:items-end">
            <button
              type="button"
              data-testid="wellbeing-open-button"
              onClick={() => window.dispatchEvent(new Event("open-wellbeing"))}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-cream text-navy-deep px-7 py-4 text-sm font-medium hover:bg-cream-soft hover:-translate-y-0.5 transition-all duration-300 shadow-[0_18px_38px_-18px_rgba(0,0,0,0.5)]"
            >
              Take the wellbeing check <ArrowRight size={14} />
            </button>
            <p className="text-[11px] text-cream/60 lg:text-right max-w-[260px]">
              For reflection only — not a clinical diagnosis.
            </p>
          </div>
        </motion.div>
      </section>

      {/* LOCATIONS */}
      <section id="locations" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12 mb-14">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] text-navy mb-4">Where we meet</div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-tight text-ink">
              Two locations, plus online.
            </h2>
          </motion.div>
          <motion.div {...fadeUp} className="lg:col-span-6 lg:col-start-7">
            <p className="text-ink-muted leading-relaxed font-light">
              I hold space in warm, community-run neighbourhood houses near Sorell, and
              via secure Zoom sessions for anyone in Tasmania or beyond.
            </p>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {locations.map((loc, i) => (
            <motion.div
              key={loc.slug}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.1 }}
              data-testid={`location-card-${loc.slug}`}
              className="relative bg-cream-soft rounded-[2rem] p-7 border border-sand/70 hover:border-navy transition-colors duration-500 flex flex-col"
            >
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-navy mb-5">
                {loc.type === "online" ? <Video size={14} /> : <MapPin size={14} />}
                {loc.type === "online" ? "Telehealth" : "In person"}
              </div>
              <h3 className="font-serif text-2xl leading-tight text-ink mb-3">{loc.name}</h3>
              <p className="text-sm text-ink-muted leading-relaxed font-light flex-grow">
                {loc.description}
              </p>
              <div className="mt-6 pt-5 border-t border-sand/50 text-xs text-ink-soft leading-relaxed">
                {loc.address}
              </div>
              <Link
                to={`/book?location=${loc.slug}`}
                data-testid={`location-book-${loc.slug}`}
                className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-navy hover:gap-3 transition-all"
              >
                <CalendarIcon size={14} /> See availability
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CRISIS / EMERGENCY HELP */}
      <section
        id="crisis"
        data-testid="crisis-section"
        className="relative bg-cream-soft border-t border-sand/60"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <motion.div {...fadeUp} className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-2 rounded-full bg-navy/8 text-navy px-4 py-1.5 text-xs uppercase tracking-[0.22em] mb-5">
              <LifeBuoy size={14} />
              {c("crisis.eyebrow", "If you need help right now")}
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight text-ink mb-5">
              {c("crisis.headline", "You're not alone — and you don't have to wait.")}
            </h2>
            <p className="text-ink-muted leading-relaxed font-light">
              {c(
                "crisis.body",
                "Counselling isn't an emergency service. If you or someone you love is in immediate danger, or struggling to keep yourself safe right now, please reach out to one of the services below. They are free, confidential, and available 24/7."
              )}
            </p>
          </motion.div>

          {/* 000 emergency banner */}
          <motion.div
            {...fadeUp}
            data-testid="crisis-000-banner"
            className="bg-navy-deep text-cream rounded-3xl p-6 md:p-8 mb-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6"
          >
            <div className="flex-shrink-0 h-12 w-12 rounded-2xl bg-cream/10 text-teal-mist flex items-center justify-center">
              <AlertTriangle size={22} strokeWidth={1.5} />
            </div>
            <div className="flex-grow">
              <div className="text-[11px] uppercase tracking-[0.24em] text-teal-mist mb-1">
                Life-threatening emergency
              </div>
              <div className="font-serif text-2xl text-cream">
                {c("crisis.footnote", "In a life-threatening emergency, always call 000.")}
              </div>
            </div>
            <a
              href="tel:000"
              data-testid="crisis-call-000"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-cream text-navy-deep px-7 py-3.5 text-sm font-semibold hover:bg-cream-soft hover:-translate-y-0.5 transition-all whitespace-nowrap"
            >
              <Phone size={14} /> Call 000
            </a>
          </motion.div>

          {/* Resource grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CRISIS_LINES.map((r, i) => (
              <motion.div
                key={r.name}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: (i % 3) * 0.06 }}
                data-testid={`crisis-card-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-cream rounded-2xl border border-sand/70 p-6 flex flex-col hover:border-navy/60 hover:-translate-y-0.5 transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="font-serif text-xl text-ink leading-tight">{r.name}</div>
                  {r.badge && (
                    <span className="text-[10px] uppercase tracking-[0.18em] text-navy bg-navy/8 rounded-full px-2.5 py-1">
                      {r.badge}
                    </span>
                  )}
                </div>
                <p className="text-sm text-ink-muted leading-relaxed font-light flex-grow mb-4">
                  {r.desc}
                </p>
                <a
                  href={r.href}
                  className="inline-flex items-center gap-2 text-navy font-medium text-base hover:gap-3 transition-all"
                  data-testid={`crisis-phone-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Phone size={14} /> {r.phone}
                </a>
                <a
                  href={r.weburl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 text-xs text-ink-soft hover:text-navy transition-colors"
                >
                  {r.web} ↗
                </a>
              </motion.div>
            ))}
          </div>

          <p className="mt-10 text-center text-xs text-ink-soft max-w-xl mx-auto">
            These services are independent of A Mindful Space Counselling and are
            provided as a list for your safety. They are free to call from anywhere in Australia.
          </p>
        </div>
      </section>

      {/* ENQUIRY + CTA */}
      <section id="enquiry" className="relative overflow-hidden bg-navy-deep">
        <div className="absolute inset-0 opacity-30 mix-blend-soft-light pointer-events-none" aria-hidden>
          <img src={BRAND.natureUrl} alt="" className="w-full h-full object-cover" />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <motion.div {...fadeUp} className="text-center text-cream mb-12">
            <div className="text-xs uppercase tracking-[0.22em] text-teal-mist mb-4">Not sure yet?</div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-tight mb-6">
              Send a quiet enquiry first.
            </h2>
            <p className="text-cream/75 leading-relaxed font-light mx-auto max-w-xl">
              You don't need to have the words. Just tell me a bit about what's going on,
              or ask anything at all about how sessions work. I read and reply to every
              message personally — usually within one business day.
            </p>
          </motion.div>

          <motion.div {...fadeUp}>
            <div className="bg-cream rounded-[2rem] p-8 lg:p-10 shadow-[0_40px_100px_-40px_rgba(0,0,0,0.6)]">
              <div className="font-serif text-2xl text-ink mb-6 text-center">Get in touch</div>
              <EnquiryForm />
              <div className="mt-6 pt-6 border-t border-sand/60 text-center">
                <div className="text-[11px] uppercase tracking-[0.22em] text-ink-soft mb-3">
                  Or email directly
                </div>
                <div className="inline-flex">
                  <EmailCopy
                    email={BRAND.email}
                    tone="light"
                    testid="enquiry-section-email"
                  />
                </div>
              </div>
            </div>
            <div className="mt-8 text-center text-sm">
              <Link
                to="/book"
                className="text-cream/70 hover:text-teal-mist underline underline-offset-4 transition-colors"
              >
                Prefer to book straight away? Jump to the calendar →
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
